import { Placement, useFloating } from '@floating-ui/react';
import { Popover, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { Fragment } from 'react';

export interface PopoverMenuItem {
  icon?: string;
  label: React.ReactNode;
  onClick: () => void;
  value?: string;
  className?: string;
}

export interface PopoverMenuProps {
  button: JSX.Element;
  items: PopoverMenuItem[];
  headerMenu?: JSX.Element;
  placement?: Placement;
  classes?: {
    container?: string;
    panel?: string;
  };
}

export const PopoverMenu = ({
  button,
  items,
  classes,
  headerMenu,
  placement,
}: PopoverMenuProps) => {
  const { refs, floatingStyles } = useFloating({ placement });

  return (
    <Popover
      className={classNames(
        'relative',
        {
          'mt-auto': !classes?.container?.includes('mt-') && !classes?.container?.includes('m-'),
          'w-full': !classes?.container?.includes('w-'),
        },
        classes?.container,
      )}
    >
      {() => (
        <>
          <Popover.Button className="w-full relative focus:outline-none" ref={refs.setReference}>
            {button}
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              style={floatingStyles}
              className={classNames(
                {
                  'w-full': !classes?.panel?.includes('w-'),
                  'p-2': !classes?.panel?.includes('p-'),
                },
                classes?.panel,
              )}
              ref={refs.setFloating}
            >
              {({ close }) => (
                <div className="py-2 bg-white rounded">
                  {headerMenu && headerMenu}
                  {items.map(({ label, onClick, icon, className }, index) => (
                    <button
                      key={index}
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        onClick();
                        close();
                      }}
                      className={classNames(
                        'w-full bg-white items-center text-gray-900 text-left',
                        'text-[14px] leading-[20px] px-4 py-[6px]',
                        className,
                        {
                          'hover:bg-gray-900/12':
                            !className?.includes('hover:bg-') && !className?.includes('hover:!bg-'),
                          'flex gap-3': !className?.includes('block'),
                        },
                      )}
                    >
                      {icon && <img src={icon} alt="Loading..." />}
                      <div className="text-[14px] leading-[20px] font-medium">{label}</div>
                    </button>
                  ))}
                </div>
              )}
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};
